import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { A11yModule } from '@angular/cdk/a11y';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { UtilityDockStore } from './utility-dock.store';
import { NotificationsStore } from './notifications/notifications.store';
import { InboxStore } from './inbox/inbox.store';
import { NotificationsComponent } from './notifications/notifications.component';
import { InboxComponent } from './inbox/inbox.component';
import { SwipeToCloseDirective } from '@shared/directives/swipe-to-close.directive';
import { DrawerMobileCloseComponent } from '@shared/components/drawer-mobile-close/drawer-mobile-close.component';
import { UI_TEXT } from '@app/constants/ui-text.constants';
import { UTILITY_DOCK } from '@app/constants/utility-dock.constants';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { PrivateTaskRailComponent } from '@features/work/components/private-task-rail.component';

@Component({
  selector: 'app-utility-dock',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzDrawerModule,
    NzIconModule,
    NzButtonModule,
    NzBadgeModule,
    NzToolTipModule,
    NzSegmentedModule,
    A11yModule,
    TranslatePipe,
    NotificationsComponent,
    InboxComponent,
    PrivateTaskRailComponent,
    SwipeToCloseDirective,
    DrawerMobileCloseComponent,
  ],
  templateUrl: './utility-dock.component.html',
  styleUrl: './utility-dock.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UtilityDockComponent {
  protected readonly dock = inject(UtilityDockStore);
  private readonly inboxStore = inject(InboxStore);
  private readonly notificationsStore = inject(NotificationsStore);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly UI_TEXT = UI_TEXT;
  protected readonly UTILITY_DOCK = UTILITY_DOCK;

  // Responsive drawer width (desktop: 480px, tablet: 400px, mobile: fullscreen)
  protected readonly drawerWidth = signal<string | number>(480);
  // Height used when drawer is placed at the bottom (mobile sheet)
  protected readonly drawerHeight = signal<string | number>('92vh');
  // Placement: 'right' on desktop/tablet, 'bottom' sheet on mobile
  protected readonly drawerPlacement = signal<'right' | 'bottom'>('right');
  // Track mobile state for fullscreen drawer class
  protected readonly isMobile = signal<boolean>(window.innerWidth < 768);

  // Track previous drawer open state to prevent load loops
  private inboxWasOpen = signal(false);
  private notificationsWasOpen = signal(false);

  constructor() {
    this.initializeResponsiveWidth();
    this.initializeDrawerLoaders();
  }

  protected toggleInbox(): void {
    this.dock.toggleInbox();
  }

  protected toggleNotifications(): void {
    this.dock.toggleNotifications();
  }

  protected togglePrivateTasks(): void {
    this.dock.togglePrivateTasks();
  }

  protected focusPrivateTaskComposer(): void {
    this.dock.requestInboxQuickAddFocus();
  }

  protected getInboxCount(): number {
    return this.inboxStore.filteredItems().length;
  }

  protected getNotificationsCount(): number {
    return this.notificationsStore.unreadCount();
  }

  protected markAllNotificationsRead(): void {
    this.notificationsStore.markAllRead();
  }

  private initializeDrawerLoaders(): void {
    effect(() => {
      const inboxOpen = this.dock.inboxOpen();
      const notificationsOpen = this.dock.notificationsOpen();

      // Only load on transition from closed to open
      if (inboxOpen && !this.inboxWasOpen()) {
        this.inboxStore.load(false);
      }

      if (notificationsOpen && !this.notificationsWasOpen()) {
        this.notificationsStore.resetFilterType();
        this.notificationsStore.load(false);
      }

      // Update previous state
      this.inboxWasOpen.set(inboxOpen);
      this.notificationsWasOpen.set(notificationsOpen);
    });
  }

  private initializeResponsiveWidth(): void {
    // Desktop: 480px, Tablet: 400px, Mobile: fullscreen (100vw)
    const updateWidth = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      this.isMobile.set(mobile);

      const placement: 'right' | 'bottom' = mobile ? 'bottom' : 'right';
      // nz-drawer cannot re-layout an open drawer when placement flips,
      // which leaves a hybrid right/sheet state with cut-off content.
      if (placement !== this.drawerPlacement()) {
        this.closeAllDrawers();
      }

      if (mobile) {
        // Mobile: Fullscreen (100vw) bottom sheet
        this.drawerWidth.set('100vw');
      } else if (width < 1024) {
        // Tablet: 400px right drawer
        this.drawerWidth.set(400);
      } else {
        // Desktop: 480px right drawer
        this.drawerWidth.set(480);
      }
      this.drawerPlacement.set(placement);
    };

    // Initial calculation
    updateWidth();

    // Update on window resize
    window.addEventListener('resize', updateWidth);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', updateWidth));
  }

  private closeAllDrawers(): void {
    if (this.dock.inboxOpen()) {
      this.dock.closeInbox();
    }
    if (this.dock.notificationsOpen()) {
      this.dock.closeNotifications();
    }
    if (this.dock.privateTasksOpen()) {
      this.dock.closePrivateTasks();
    }
  }
}
